import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api.js';
import { useI18n } from '../i18n/index.jsx';
import { countryName } from '../productOptions.js';
import Icon from '../components/Icon.jsx';
import { Avatar } from './ConversationList.jsx';
import { StatusText } from './Presence.jsx';

// "New message": find a member by company name, contact name or product,
// then open (or start) the conversation. Members who turned "can be
// found" off, and blocks either way, never appear.
export default function NewMessage({ onClose }) {
  const { t, lang, errorText } = useI18n();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [active, setActive] = useState(0);
  const input = useRef(null);
  useEffect(() => { input.current?.focus(); const k = (e) => { if (e.key === 'Escape') onClose(); }; document.addEventListener('keydown', k); return () => document.removeEventListener('keydown', k); }, [onClose]);
  useEffect(() => {
    if (q.trim().length < 2) { setRows(null); return undefined; }
    let alive = true;
    const tm = setTimeout(() => api(`/chat/directory?q=${encodeURIComponent(q.trim())}`).then((r) => { if (alive) { setRows(r); setActive(0); } }).catch((e) => alive && setError(errorText(e))), 250);
    return () => { alive = false; clearTimeout(tm); };
  }, [q, errorText]);
  async function open(m) {
    try {
      const r = await api('/chat/conversations', { method: 'POST', body: { userId: m.id } });
      onClose(); navigate(`/account/messages/${r.id}`);
    } catch (e) { setError(errorText(e)); }
  }
  const onKey = (e) => {
    if (!rows?.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(rows.length - 1, i + 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
    if (e.key === 'Enter') { e.preventDefault(); open(rows[active]); }
  };
  return (
    <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal chat-modal" role="dialog" aria-modal="true" aria-labelledby="nm-title">
        <div className="modal-head"><h2 id="nm-title">{t('chat.newMessage')}</h2><button type="button" className="btn btn-link" onClick={onClose} aria-label={t('common.close')}>✕</button></div>
        <div className="modal-body chat-modal-body chat-dir">
          <label className="chat-search"><Icon name="search" size={17} /><span className="sr-only">{t('chat.dirSearch')}</span>
            <input ref={input} type="search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} placeholder={t('chat.dirSearch')} aria-controls="nm-list" /></label>
          {error && <p className="field-error small">{error}</p>}
          {rows === null ? <p className="muted small">{t('chat.dirHint')}</p> : !rows.length ? <p className="muted small">{t('chat.dirNone')}</p> : (
            <ul className="chat-dir-list" id="nm-list">
              {rows.map((m, i) => (
                <li key={m.id}>
                  <button type="button" onClick={() => open(m)} className={i === active ? 'is-active' : ''} onMouseEnter={() => setActive(i)} style={i === active ? { borderColor: '#a8833f' } : undefined}>
                    <Avatar person={m} size={42} status={m.presence} />
                    <span className="chat-dir-main">
                      <span><strong>{m.company || m.name}</strong>{m.verified && <span className="chat-dir-verified">✓ {t('accountNav.verified')}</span>}</span>
                      <span className="small muted">{[m.name, m.city, m.country && countryName(m.country, lang)].filter(Boolean).join(' · ')}</span>
                      {m.product && <span className="small">🛍 {m.product}</span>}
                    </span>
                    {m.presence && <StatusText status={m.presence} />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
